import { WarningPanelType } from "@kuika/kuika-cl-model";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../../shared/pure-sd";
import {
  DESIGN_CHANGED_EVENT,
  getErrorCounts,
  IErrorCounts,
  VALIDATION_RAN_EVENT
} from "../../../../../shared/utility/issues/issues-model";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaPopover from "../../../../ui-kit/kuika-popover/kuika-popover";
import { KuikaSkeletonTable } from "../../../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import { IssuesPanel } from "./issues-panel";
import "./issues.scss";

/**
 * Floor between validate-on-open auto-runs. A run costs what Preview costs server-side
 * (~18s typical on large projects), so an edit-open-close-open loop must not fire one per
 * open; the dirty gate (validationFresh) already blocks re-runs on a clean design.
 */
const AUTO_VALIDATE_COOLDOWN_MS: number = 60_000;

export interface IIssuesButtonProps {}

export interface IIssuesButtonState {
  visible: boolean;
  // Whether the popover is expanded to a large (~half-screen) in-place panel — used
  // to read long code-generation errors without leaving the popover.
  expanded: boolean;
  // Whether the CURRENT screen has been validated by a preview and not edited since. The
  // green "all-clear ✓" only shows while this is true — a clean state before any run just means
  // "not validated yet", not "no problems". Set by VALIDATION_RAN, cleared by DESIGN_CHANGED.
  // (Errors/warnings show regardless — they're real diagnostics from the last run.)
  validationFresh: boolean;
}

/**
 * Top-bar Issues trigger — a toolbar button showing the diagnostics count, opening
 * the IssuesPanel popover (KOA-16942). Only renders when diagnostics exist (post-run).
 * The popover can expand in place (see `expanded`) to comfortably read long errors.
 */
class IssuesButton extends PureSdComponent<IIssuesButtonProps & IWithSDProps, IIssuesButtonState> {
  constructor(props: IIssuesButtonProps & IWithSDProps) {
    super(props);
    this.state = { visible: false, expanded: false, validationFresh: false };
  }

  componentDidMount(): void {
    window.addEventListener(VALIDATION_RAN_EVENT, this.onValidationRan);
    window.addEventListener(DESIGN_CHANGED_EVENT, this.onDesignChanged);
  }

  componentWillUnmount(): void {
    window.removeEventListener(VALIDATION_RAN_EVENT, this.onValidationRan);
    window.removeEventListener(DESIGN_CHANGED_EVENT, this.onDesignChanged);
  }

  // A preview/publish produced a validation result → the all-clear ✓ may now show.
  private onValidationRan = (): void => {
    if (!this.state.validationFresh) this.setState({ validationFresh: true });
  };

  // The design changed (edit / style / hide-lock / load) → the last validation is stale, so the
  // ✓ hides until the next preview. Guarded so the frequent event only re-renders on the first
  // edit after a run.
  private onDesignChanged = (): void => {
    if (this.state.validationFresh) this.setState({ validationFresh: false });
  };

  /** Last validate-on-open trigger; local because it only rate-limits THIS trigger. */
  private lastAutoValidateAt: number = 0;

  /**
   * Last value of the "surface the diagnostics now" signal. The codegen SSE flow and the preview
   * response raise `warningPanelType` to Open when a run is blocked (see project-provider), and
   * the old footer panel was its reader. Removing the footer left the signal emitted with nothing
   * listening, so a blocked preview only lit this badge and waited to be noticed. Opening on the
   * Close -> Open edge restores that, and edge-only means a popover the user closes stays closed
   * until the next run raises it again.
   */
  private lastWarningPanelType: WarningPanelType = WarningPanelType.Close;

  componentDidUpdate(): void {
    const current: WarningPanelType = this.props.sd.getWarningPanelType();
    if (current === this.lastWarningPanelType) return;
    this.lastWarningPanelType = current;
    if (current === WarningPanelType.Open) {
      this.setVisible(true);
    }
  }

  private setVisible = (visible: boolean): void => {
    // Reset the expanded state when the popover closes so it reopens compact.
    if (visible) {
      this.setState({ visible: true });
      this.maybeAutoValidate();
    } else {
      // Deliberately no cancel on close: the run is single-flighted and the server finishes
      // and caches it regardless — its result still lands in the badge and the next open.
      this.setState({ visible: false, expanded: false });
    }
  };

  /**
   * Validate-on-open: run the project validation only when the last result is stale
   * (design edited since — validationFresh) and outside the cooldown. The provider adds
   * its own guards (single-flight join, no-op while a codegen generation is active).
   */
  private maybeAutoValidate = (): void => {
    if (this.state.validationFresh) return;
    const now: number = Date.now();
    if (now - this.lastAutoValidateAt < AUTO_VALIDATE_COOLDOWN_MS) return;
    this.lastAutoValidateAt = now;
    void this.props.sd.requestIssuesValidation();
  };

  private setExpanded = (expanded: boolean): void => {
    this.setState({ expanded });
  };

  render() {
    const counts: IErrorCounts = getErrorCounts(this.props.sd);
    const totalCount: number = counts.errorCount + counts.warningCount;
    // The Issues control is a permanent member of the action container so the row's layout is
    // stable. The count chip only appears once a run/preview has produced diagnostics
    // (validationErrors/datasource/codeGeneration, populated by the codegen SSE flow); with none,
    // it's a plain bug icon that still opens the (empty) panel.
    const hasIssues: boolean = totalCount > 0;
    // A standalone validation on the wire owns the corner: a spinner badge shows alone
    // (no error/warning/refresh badge until the run lands) and the popover body is a skeleton.
    const isValidating: boolean = this.props.sd.isProjectValidationRunning;
    // Fixed square control; the label is a tooltip so it never widens the row. Spells out the
    // severity split the two corner badges show.
    let label: string;
    if (isValidating) {
      label = "Validating project…";
    } else if (hasIssues) {
      const parts: string[] = [];
      if (counts.errorCount > 0) parts.push(`${counts.errorCount} error${counts.errorCount > 1 ? "s" : ""}`);
      if (counts.warningCount > 0) parts.push(`${counts.warningCount} warning${counts.warningCount > 1 ? "s" : ""}`);
      label = parts.join(", ");
    } else if (this.state.validationFresh) {
      label = "No issues";
    } else {
      label = "Open to validate";
    }

    return (
      <KuikaPopover
        visible={this.state.visible}
        onVisibleChange={this.setVisible}
        position="bottom"
        align="start"
        sideOffset={8}
        collisionPadding={12}
        hideCloseIcon
        className={`kuika-issues__popover${this.state.expanded ? " kuika-issues__popover--expanded" : ""}`}
        content={
          isValidating ? (
            // A run is on the wire (~18s server-side): the body is a row-shaped skeleton —
            // stale diagnostics are not shown while a fresh set is being computed.
            <div className="kuika-issues__skeleton" data-testid="issuesValidatingSkeleton" aria-busy="true">
              <KuikaSkeletonTable rows={5} rowHeight={28} header />
            </div>
          ) : (
            <IssuesPanel
              onNavigate={() => this.setVisible(false)}
              expanded={this.state.expanded}
              onSetExpanded={this.setExpanded}
            />
          )
        }
      >
        {/* Open state is OUR modifier, not KuikaButton's `active` — same reason as the branch
            selector (KOA-18355): the neutral variant's `active` is a GREY fill with an
            `!important` glyph, so the trigger would drop out of its engaged blue the moment its
            own popover opened. */}
        <KuikaButton
          data-testid="toolbarIssuesButton"
          type="secondary"
          color="neutral"
          size="sm"
          className={`kuika-issues__trigger${this.state.visible ? " kuika-issues__trigger--open" : ""}`}
          aria-label={label}
        >
          <div className="kuika-flex-center" title={label} aria-busy={isValidating}>
            {/* No `color` prop: KuikaSvg writes --kuika-svg-color INLINE, which no hover rule can
                outrank. The glyph rests neutral from the button variant and turns blue from CSS. */}
            <KuikaSvg name="bug" width={18} height={18} />
            {/* Corner badges (Figma 1851:102452 / 1851:102589). While a validation is on the
                wire the spinner badge shows ALONE; otherwise errors ride top-RIGHT, warnings
                top-LEFT (bottom-right when stacked with errors). A clean set shows nothing: the
                corner stays quiet until there is something to say, and the label says the rest.
                The trigger itself never changes size. */}
            {isValidating && <span className="kuika-issues__corner-badge kuika-issues__corner-badge--busy" />}
            {!isValidating && counts.errorCount > 0 && (
              <span className="kuika-issues__corner-badge kuika-issues__corner-badge--error">{counts.errorCount}</span>
            )}
            {!isValidating && counts.warningCount > 0 && (
              <span
                className={`kuika-issues__corner-badge kuika-issues__corner-badge--warning${
                  counts.errorCount > 0 ? " kuika-issues__corner-badge--stacked" : ""
                }`}
              >
                {counts.warningCount}
              </span>
            )}
          </div>
        </KuikaButton>
      </KuikaPopover>
    );
  }

  // Re-render on any SD change so the button's visibility tracks validationErrors
  // (which are refreshed by the codegen SSE flow, not a single dedicated reason).
  checkNeedRender = (): boolean => true;
}

const issuesButton = withSD<IIssuesButtonProps>(IssuesButton);
export { issuesButton as IssuesButton };
